import { VoidCallback } from '@deqo/foundation';
import { darken } from 'polished';
import React, { useState } from 'react';
import { useEffectOnce } from 'react-use';
import styled, { css } from 'styled-components';
import { useParams } from 'wouter';
import { Images } from '../../@resources/img';
import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { SurveyAnswer, SurveyQuestion } from '../../api/endpoints/surveys';
import { useLoader, useSubmitter } from '../../platform/Hook';
import { Button, Cell, Colors, Float, Grid } from '../../ui';
import { LoadingFrame } from '../../ui/LoadingGuard';
import { AppNavigator } from '../AppNavigator';
import { useResolve } from '../Container';
import { Flexer } from '../dashboard/elements/LayoutElements';

const name = 'account_onboarding_survey';

const Root = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100vh;
    background-color: #EFF3F6;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    padding-bottom: 1rem;
`;

const Logo = styled.div`
    width: 5rem;
    height: 5rem;
    box-sizing: border-box;
    overflow: hidden;
    background: url(${Images.LogoBlue}) center center no-repeat;
`;

const Brand = styled.div`
    margin-left: 0.5rem;
    color: ${Colors.primary};
    font-family: 'Rubik';
    font-weight: 500;
    font-size: 2.6rem;
`;

const ProgressBar = styled.div<{ active?: boolean }>`
    margin: 0 0.75rem 0 0.75rem;
    height: 0.25rem;
    background: ${props => props.active ? Colors.primary : '#D3D3D3'};
`;

const Question = styled.div<{ size: number, opacity: number }>`
    font-weight: 450;
    font-size: ${props => props.size}rem;
    opacity: ${props => props.opacity};
`;

const optionStyles = css`
    font-size: 0.875rem;
    line-height: 2rem;
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
    border-radius: 2px;
`;

const Option = styled.div<{ selected?: boolean }>`
    width: 12rem;
    padding: 0.125rem;
    text-align: center;
    border: 1px solid;
    border-color: ${props => props.selected ? Colors.primary : '#D2D6DC'};
    cursor: pointer;

    &:hover { background: ${darken(0.025, '#fff')} }
    &:focus { border-color: ${Colors.blue300} }
    &:active { background: ${darken(0.05, '#fff')} }

    ${optionStyles}
`;

const OptionInputBox = styled.input`
    width: 100%;
    height: 100%;
    padding: 0 0.5rem;
    border: 1px solid ${Colors.primary};

    ${optionStyles}
`;

const OptionInput: React.FC<{
    question: SurveyQuestion,
    answers: SurveyAnswer[],
    onChange: (answer: SurveyAnswer) => void,
    onApply: VoidCallback,
}> = ({ question, answers, onChange, onApply }) => {
    const placeholder = 'Enter text here';
    const answer = answers.find(a => a.questionId === question.id && a.id === 'other');

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            if (answer.label !== '') {
                e.preventDefault();
                onApply();
            }
        }
    };

    return (
        <OptionInputBox
            type="text"
            placeholder={placeholder}
            value={answer.label}
            onKeyDown={onKeyDown}
            onChange={e => onChange({
                id: 'other',
                questionId: question.id,
                type: question.type,
                label: e.target.value,
            })}
        />
    );
};

export const SurveyPage = () => {
    const { version } = useParams<{ version: string }>();

    const api = useResolve(ApiClient);
    const nav = useResolve(AppNavigator);

    const [loading, surveyDef] = useLoader(async () => await api.exec(v2.surveys.info(name)), []);
    const [answers, setAnswers] = useState([] as SurveyAnswer[]);
    const [current, setCurrent] = useState(0);

    useEffectOnce(() => {
        api.exec(v2.internal.setup.init()).then(() => { });
    });

    const [onSubmit, working, result] = useSubmitter(async () => {
        const id = await api.exec(v2.surveys.create({
            id: null,
            definitionId: surveyDef.id,
            answers,
        }));

        if (version === 'next') {
            nav.go(`~/setup/next?survey=${id}`);
        }
        else {
            nav.go(`~/setup?survey=${id}`);
        }

    }, [answers]);

    const onSkip = () => {
        if (version === 'next') {
            nav.go('~/setup/next');
        }
        else {
            nav.go('~/setup');
        }
    };

    const prev = () => {
        if (current > 0) {
            setCurrent(current - 1);
        }
    };

    const next = () => {
        if (current < questionTotal - 1) {
            setCurrent(current + 1);
        }
    };

    const updateOther = (answer: SurveyAnswer) => {
        const newAnswers = [...answers.filter(a =>
            !(a.questionId === answer.questionId && a.id === 'other')
        ), answer];
        setAnswers(newAnswers);
    };

    const onSelect = (answer: SurveyAnswer) => {
        if (answers.some(a => a.questionId === answer.questionId)) {
            const newAnswers = answers.map(a => a.questionId === answer.questionId ? answer : a);
            setAnswers(newAnswers);
        }
        else {
            const newAnswers = [...answers, answer];
            setAnswers(newAnswers);
            if (answer.id !== 'other') {
                next();
            }
        }
    };

    const isSelected = (qId: string, oId: string) => {
        return answers.some(a => a.questionId === qId && a.id === oId);
    };

    if (loading) return <LoadingFrame />;

    const questionTotal = surveyDef.questions.length;

    const disableButton = (qId: string) => {
        return !answers.some(a => a.questionId === qId && a.label !== '');
    };

    return <Root>
        <Header>
            <Logo />
            <Brand>Metro Retro</Brand>
        </Header>
        <Float width={44} height={34} style={{ position: 'relative' }}>
            <Flexer flex="column">
                <Flexer padding="2 2 3 2" grow>
                    <Grid columns={surveyDef.questions.length} columnGap="1">
                        {surveyDef.questions.map((q, i) =>
                            <ProgressBar key={q.id} active={i <= current} />
                        )}
                    </Grid>
                </Flexer>
                {surveyDef.questions.map(q => {
                    if (surveyDef.questions.indexOf(q) !== current) return null;
                    return <div key={q.id}>
                        <Flexer flex="column" align="center">
                            <Question size={1.5} opacity={1}>{q.label}</Question>
                            <Question size={1} opacity={.5}>{q.subLabel}</Question>
                        </Flexer>
                        <Flexer flex="column" align="center" padding="2 0 0 0">
                            <Grid columns={3} columnGap="1" rowGap="1">
                                {q.options.map(o =>
                                    <Cell key={o.id}>
                                        <Option selected={isSelected(q.id, o.id)}
                                            onClick={() => onSelect({
                                                id: o.id,
                                                questionId: q.id,
                                                type: q.type,
                                                label: o.id === 'other' ? '' : o.label,
                                            })}>
                                            {o.label}
                                        </Option>
                                    </Cell>
                                )}
                                {isSelected(q.id, 'other') &&
                                    <Cell width={2}>
                                        <OptionInput
                                            question={q}
                                            answers={answers}
                                            onChange={updateOther}
                                            onApply={next}
                                        />
                                    </Cell>}
                            </Grid>
                        </Flexer>
                    </div>;
                })}
            </Flexer>
            <div style={{ position: 'absolute', left: '3rem', bottom: '2rem' }}>
                {current !== 0 ?
                    <Button onAction={prev}>Prev</Button> :
                    <Button variant="text" onAction={onSkip}><div style={{ opacity: '0.5' }}>Skip</div></Button>
                }
            </div>
            <div style={{ position: 'absolute', right: '3rem', bottom: '2rem' }}>
                {current !== questionTotal - 1 ?
                    <Button variant="primary" disabled={disableButton(surveyDef.questions[current].id)} onAction={next}>Next</Button> :
                    <Button variant="primary" disabled={disableButton(surveyDef.questions[current].id)} onAction={onSubmit}>Submit</Button>
                }
            </div>
        </Float>
    </Root>;
};
